'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var typographyTheme = require('../../../../Theme/typographyTheme.js');
var makeStyles = require('../../../Styles/makeStyles.js');
var fade = require('../../../../utils/fade.js');
var spacing = require('../../../../constants/spacing.js');

var MONOCHROME_COLORS = ['black', 'white'];
var isMonochromeColor = function isMonochromeColor(color) {
  return MONOCHROME_COLORS.includes(color);
};
var HOVER_OPACITY = 0.2;
var CONTAINED_MONOCHROME_HOVER_OPACITY = 1 - HOVER_OPACITY;

var SMALL_SIZE_BUTTON_PADDING = function SMALL_SIZE_BUTTON_PADDING(_ref) {
  var theme = _ref.theme,
      variant = _ref.variant;

  switch (variant) {
    case 'text':
      return theme.spacing(spacing.PX_TO_MULTIPLIER['8'], spacing.PX_TO_MULTIPLIER['5']);

    case 'outlined':
      return theme.spacing(spacing.PX_TO_MULTIPLIER['7'], spacing.PX_TO_MULTIPLIER['11']);

    case 'contained':
    default:
      return theme.spacing(spacing.PX_TO_MULTIPLIER['8'], spacing.PX_TO_MULTIPLIER['12']);
  }
};

var MEDIUM_SIZE_BUTTON_PADDING = function MEDIUM_SIZE_BUTTON_PADDING(_ref2) {
  var theme = _ref2.theme,
      variant = _ref2.variant;

  switch (variant) {
    case 'text':
      return theme.spacing(spacing.PX_TO_MULTIPLIER['8'], spacing.PX_TO_MULTIPLIER['10']);

    case 'outlined':
      return theme.spacing(spacing.PX_TO_MULTIPLIER['9'], spacing.PX_TO_MULTIPLIER['15']);

    case 'contained':
    default:
      return theme.spacing(spacing.PX_TO_MULTIPLIER['10'], spacing.PX_TO_MULTIPLIER['16']);
  }
};

var LARGE_SIZE_BUTTON_PADDING = function LARGE_SIZE_BUTTON_PADDING(_ref3) {
  var theme = _ref3.theme,
      variant = _ref3.variant;

  switch (variant) {
    case 'text':
      return theme.spacing(spacing.PX_TO_MULTIPLIER['12'], spacing.PX_TO_MULTIPLIER['11']);

    case 'outlined':
      return theme.spacing(spacing.PX_TO_MULTIPLIER['11'], spacing.PX_TO_MULTIPLIER['15']);

    case 'contained':
    default:
      return theme.spacing(spacing.PX_TO_MULTIPLIER['12'], spacing.PX_TO_MULTIPLIER['16']);
  }
};

var getButtonPadding = function getButtonPadding(_ref4) {
  var size = _ref4.size,
      variant = _ref4.variant,
      theme = _ref4.theme;

  if (size === 'small') {
    return SMALL_SIZE_BUTTON_PADDING({
      theme: theme,
      variant: variant
    });
  }

  if (size === 'large') {
    return LARGE_SIZE_BUTTON_PADDING({
      theme: theme,
      variant: variant
    });
  }

  return MEDIUM_SIZE_BUTTON_PADDING({
    theme: theme,
    variant: variant
  });
};

var useStyles = makeStyles["default"](function (theme) {
  return {
    root: {
      '&:focus-visible': {
        outline: function outline(_ref5) {
          var color = _ref5.color;
          return "".concat(theme.spacing(spacing.PX_TO_MULTIPLIER['2']), "px solid ").concat(isMonochromeColor(color) ? theme.palette.common[color] : theme.palette.primary.main);
        },
        outlineOffset: '1px'
      },
      borderRadius: '200px',
      boxShadow: 'none',
      '&:hover, &:focus': {
        boxShadow: 'none'
      },
      fontFamily: typographyTheme.FONT_FAMILY,
      fontSize: '.875rem',
      lineHeight: '1rem',
      fontWeight: 600,
      padding: function padding(_ref6) {
        var size = _ref6.size,
            variant = _ref6.variant;
        return getButtonPadding({
          size: size,
          variant: variant,
          theme: theme
        });
      }
    },
    contained: {
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.text.disabled
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette.primary.main
        }
      }
    },
    containedError: {
      color: theme.palette.error.contrastText,
      backgroundColor: theme.palette.error.main,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette.error.main
        }
      }
    },
    containedBlack: {
      color: theme.palette.text.reverse,
      backgroundColor: theme.palette.common.black,
      '&:hover': {
        backgroundColor: fade(theme.palette.common.black, CONTAINED_MONOCHROME_HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette.common.black
        }
      }
    },
    containedWhite: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.common.white,
      '&:hover': {
        backgroundColor: fade(theme.palette.common.white, CONTAINED_MONOCHROME_HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette.common.white
        }
      }
    },
    outlined: {
      '&:hover': {
        backgroundColor: fade(theme.palette.primary.light, HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      },
      '&:disabled': {
        border: "1px solid ".concat(theme.palette.grey[400])
      }
    },
    outlinedError: {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.main,
      '&:hover': {
        borderColor: theme.palette.error.main,
        backgroundColor: theme.palette.error.light,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },
    outlinedBlack: {
      color: theme.palette.text.primary,
      borderColor: theme.palette.common.black,
      '&:hover': {
        borderColor: theme.palette.common.black,
        backgroundColor: fade(theme.palette.common.black, HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },
    outlinedWhite: {
      color: theme.palette.text.reverse,
      borderColor: theme.palette.common.white,
      '&:hover': {
        borderColor: theme.palette.common.white,
        backgroundColor: fade(theme.palette.common.white, HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },
    text: {
      '&:hover': {
        backgroundColor: fade(theme.palette.primary.light, HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      },
      '&:disabled': {
        color: theme.palette.text.disabled
      }
    },
    textError: {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: fade(theme.palette.error.light, HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      },
      '&:disabled': {
        color: theme.palette.text.disabled
      }
    },
    textBlack: {
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: fade(theme.palette.common.black, HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },
    textWhite: {
      color: theme.palette.text.reverse,
      '&:hover': {
        backgroundColor: fade(theme.palette.common.white, HOVER_OPACITY),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },
    label: {
      display: 'flex',
      gap: "".concat(theme.spacing(spacing.PX_TO_MULTIPLIER['4']), "px")
    },
    labelDisabled: {
      color: theme.palette.text.disabled
    },
    startIcon: {
      margin: theme.spacing(spacing.PX_TO_MULTIPLIER['0']),
      '& > *:first-child': {
        fontSize: '1rem'
      }
    },
    endIcon: {
      margin: theme.spacing(spacing.PX_TO_MULTIPLIER['0']),
      '& > *:first-child': {
        fontSize: '1rem'
      }
    }
  };
});

exports.isMonochromeColor = isMonochromeColor;
exports.useStyles = useStyles;
